import React, { useState, useEffect, useCallback } from "react";

// Inline environment config (keeps behavior of config/environment.js)
const config = {
  development: {
    apiUrl: "http://localhost:8787",
    environment: "development",
  },
  production: {
    apiUrl:
      process.env.REACT_APP_API_URL ||
      "https://property-filter-api.congdongdong03.workers.dev",
    environment: "production",
  },
};
const environment =
  process.env.NODE_ENV === "production" ? "production" : "development";
const env = config[environment];

// Global styles injection (subset of styles.css needed for main flows)
const GlobalStyles = () => (
  <style>{`
    body { font-family: "Inter", "Noto Sans SC", sans-serif; -webkit-text-size-adjust: 100%; -webkit-tap-highlight-color: transparent; touch-action: manipulation; }
    .tab-active { border-color: #4f46e5; color: #4f46e5; background-color: #eef2ff; position: relative; z-index: 10; }
    .property-card { display: flex; flex-direction: column; overflow: hidden; transition: transform 0.15s ease-in-out, box-shadow 0.15s ease-in-out; -webkit-tap-highlight-color: transparent; touch-action: manipulation; cursor: pointer; }
    .property-card:active { transform: scale(0.98); }
    #layout-container { transition: flex-direction 0.3s ease; }
    #left-panel, #right-panel { transition: width 0.3s ease; }
    .rating-criteria-item { border-radius: 16px; border: 2px solid #e5e7eb; padding: 20px; margin-bottom: 16px; transition: all 0.2s ease-in-out; }
    .rating-criteria-item:hover { transform: translateY(-2px); box-shadow: 0 8px 25px rgba(0,0,0,0.1); }
    /* Modal */
    .modal-overlay { position: fixed; inset: 0; background: rgba(0,0,0,0.5); display: flex; align-items: center; justify-content: center; z-index: 100; }
    .modal-content { background: #fff; border-radius: 16px; max-width: 900px; width: calc(100% - 40px); max-height: 90vh; overflow: auto; box-shadow: 0 20px 40px rgba(0,0,0,0.2); }
    .carousel { display: flex; align-items: center; justify-content: center; position: relative; overflow: hidden; }
    .carousel img { border-radius: 12px; width: 100%; height: 420px; object-fit: contain; background: #0f172a0d; }
    .carousel > button { position: absolute; top: 50%; transform: translateY(-50%); z-index: 2; background: rgba(255,255,255,0.9); border: 1px solid #e5e7eb; box-shadow: 0 2px 8px rgba(0,0,0,0.08); }
    .carousel > button:first-of-type { left: 8px; }
    .carousel > button:last-of-type { right: 8px; }
  `}</style>
);

// Inline hooks: useCriteria
const useCriteria = () => {
  const [criteria, setCriteria] = useState([]);

  const fetchCriteria = useCallback(async () => {
    try {
      const response = await fetch(`${env.apiUrl}/api/criteria`);
      if (!response.ok)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const data = await response.json();
      setCriteria(data);
    } catch (err) {
      console.error("获取筛选条件失败:", err);
    }
  }, []);

  const addCriteria = useCallback(async (criteriaData) => {
    try {
      const response = await fetch(`${env.apiUrl}/api/criteria`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(criteriaData),
      });
      if (!response.ok) throw new Error("Failed to add criteria");
      try {
        await response.json();
      } catch {}
      await fetchCriteria();
      return true;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }, []);

  const deleteCriteria = useCallback(async (id) => {
    try {
      const response = await fetch(`${env.apiUrl}/api/criteria/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete criteria");
      await fetchCriteria();
    } catch (err) {
      console.error(err);
      throw err;
    }
  }, []);

  useEffect(() => {
    fetchCriteria();
  }, [fetchCriteria]);

  return { criteria, addCriteria, deleteCriteria };
};

// Inline hooks: useProperties
const useProperties = () => {
  const [properties, setProperties] = useState([]);

  const fetchProperties = useCallback(async () => {
    try {
      const response = await fetch(`${env.apiUrl}/api/properties`);
      if (!response.ok)
        throw new Error(`HTTP ${response.status}: ${response.statusText}`);
      const data = await response.json();
      setProperties(data);
    } catch (err) {
      console.error("获取房源失败:", err);
    }
  }, []);

  const addProperty = useCallback(async (propertyData) => {
    try {
      const response = await fetch(`${env.apiUrl}/api/properties`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(propertyData),
      });
      if (!response.ok) throw new Error("Failed to add property");
      const newProperty = await response.json();
      setProperties((prev) => [...prev, newProperty]);
      return newProperty;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }, []);

  const updateProperty = useCallback(async (id, updates) => {
    try {
      const response = await fetch(`${env.apiUrl}/api/properties/${id}`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(updates),
      });
      if (!response.ok) throw new Error("Failed to update property");
      const updatedProperty = await response.json();
      setProperties((prev) =>
        prev.map((p) => (p.id === id ? updatedProperty : p))
      );
      return updatedProperty;
    } catch (err) {
      console.error(err);
      throw err;
    }
  }, []);

  const deleteProperty = useCallback(async (id) => {
    try {
      const response = await fetch(`${env.apiUrl}/api/properties/${id}`, {
        method: "DELETE",
      });
      if (!response.ok) throw new Error("Failed to delete property");
      setProperties((prev) => prev.filter((p) => p.id !== id));
    } catch (err) {
      console.error(err);
      throw err;
    }
  }, []);

  useEffect(() => {
    fetchProperties();
  }, [fetchProperties]);

  return {
    properties,
    fetchProperties,
    addProperty,
    updateProperty,
    deleteProperty,
  };
};

// Inline components used in main flows: RatingCriteria, PropertyCard, PropertyList, PropertyForm
const RatingCriteria = ({
  criteria,
  ratings,
  onRatingChange,
  onAddCriteria,
  onDeleteCriteria,
}) => {
  const handleAddCriteria = async () => {
    const newNumber = criteria.length + 1;
    const newLabel = prompt(
      `请输入第${newNumber}个评分标准的描述：\n例如：采光好、停车方便、学区房等`
    );
    if (newLabel && newLabel.trim()) {
      try {
        await onAddCriteria({
          id: `custom_${Date.now()}`,
          label: `${newNumber}. ${newLabel.trim()}`,
        });
      } catch {}
    }
  };

  const handleManageCriteria = async () => {
    const customCriteria = criteria.filter((c) => c.id.startsWith("custom_"));
    if (customCriteria.length === 0) {
      alert("目前没有自定义的评分标准。");
      return;
    }
    let message = "自定义评分标准：\n\n";
    customCriteria.forEach((c, i) => (message += `${i + 1}. ${c.label}\n`));
    message += "\n要删除某个标准，请输入对应的编号：";
    const input = prompt(message);
    if (input && !isNaN(input)) {
      const index = parseInt(input) - 1;
      if (index >= 0 && index < customCriteria.length) {
        const toDelete = customCriteria[index];
        if (
          window.confirm(
            `确定要删除"${toDelete.label}"吗？\n注意：删除后，所有房源的这项评分也会被删除。`
          )
        ) {
          try {
            await onDeleteCriteria(toDelete.id);
          } catch {}
        }
      } else {
        alert("无效的编号！");
      }
    }
  };

  const renderRatingSlider = (criteriaId, currentRating) => {
    return (
      <div className="flex items-center gap-3 w-full">
        <input
          type="range"
          min="1"
          max="10"
          step="1"
          value={currentRating || 5}
          onChange={(e) => onRatingChange(criteriaId, Number(e.target.value))}
          className="flex-1 accent-pink-600"
          aria-label={`评分: ${currentRating || 5}`}
        />
        <span className="text-lg font-bold text-pink-600 min-w-[2.5rem] text-center">
          {currentRating || 5}
        </span>
      </div>
    );
  };

  return (
    <div>
      <h3 className="text-lg font-semibold text-gray-800 mb-3">
        评分 (1~10分)
      </h3>
      <div className="space-y-4">
        {criteria.map((c) => (
          <div
            key={c.id}
            className="rating-criteria-item bg-white p-4 rounded-lg border border-gray-200 shadow-sm"
          >
            <label className="block text-sm font-medium text-gray-700 mb-3">
              {c.label}
            </label>
            {renderRatingSlider(c.id, ratings[c.id])}
          </div>
        ))}
      </div>
      <div className="mt-6 grid grid-cols-2 gap-3">
        <button
          type="button"
          onClick={handleAddCriteria}
          className="w-full bg-green-100 text-green-800 font-bold py-3 px-4 rounded-lg hover:bg-green-200 active:scale-95 transition-all"
        >
          ➕ 新增评分项
        </button>
        <button
          type="button"
          onClick={handleManageCriteria}
          className="w-full bg-yellow-100 text-yellow-800 font-bold py-3 px-4 rounded-lg hover:bg-yellow-200 active:scale-95 transition-all"
        >
          ⚙️ 管理评分项
        </button>
      </div>
    </div>
  );
};

const PropertyCard = ({ property, criteria, onShowModal }) => (
  <div
    className="property-card bg-white rounded-2xl shadow-lg border border-gray-200 p-6 hover:shadow-xl"
    onClick={() => onShowModal(property)}
    role="button"
    tabIndex={0}
  >
    <div className="mb-4">
      <div className="mb-3">
        {Array.isArray(property.images) && property.images.length > 0 ? (
          <img
            src={property.images[0]}
            alt="房源图片"
            className="w-full h-40 object-cover rounded-xl border border-gray-200"
            onError={(e) => {
              e.currentTarget.src =
                "https://placehold.co/600x360/e2e8f0/94a3b8?text=No+Image";
            }}
          />
        ) : (
          <img
            src="https://placehold.co/600x360/e2e8f0/94a3b8?text=No+Image"
            alt="无图片"
            className="w-full h-40 object-cover rounded-xl border border-gray-200"
          />
        )}
      </div>
      <p className="text-sm text-indigo-500 font-semibold mb-2">
        {property.suburb}
      </p>
      <h3
        className="font-bold text-gray-800 text-xl mb-2"
        title={property.address}
      >
        {property.address}
      </h3>
      <p className="text-base text-gray-500">第{property.tier}梯队</p>
    </div>
    <div className="space-y-3 mb-4">
      {criteria.map((criterion) => (
        <div
          key={criterion.id}
          className="flex justify-between items-center py-2 border-b border-gray-100 last:border-b-0"
        >
          <span className="text-base text-gray-700">{criterion.label}</span>
          <span className="font-bold text-lg text-indigo-600">
            {property.ratings?.[criterion.id] || 5}/10
          </span>
        </div>
      ))}
    </div>
    {property.remarks ? (
      <p className="text-base bg-gray-50 p-4 rounded-xl text-gray-700 whitespace-pre-wrap min-h-[72px] leading-relaxed">
        {property.remarks}
      </p>
    ) : (
      <p className="text-base bg-gray-50 p-4 rounded-xl text-gray-700 min-h-[72px] leading-relaxed text-center flex items-center justify-center">
        暂无备注
      </p>
    )}
    {/* 去掉卡片内的操作按钮，改为点击整卡进入弹层 */}
  </div>
);

const PropertyList = ({
  properties,
  criteria,
  activeTab,
  onTabSwitch,
  onShowModal,
}) => {
  const tier1Props = properties.filter((p) => Number(p.tier) === 1);
  const tier2Props = properties.filter((p) => Number(p.tier) === 2);
  const renderTier = (list) => {
    if (list.length === 0)
      return <div className="text-center text-gray-500 py-10">暂无房源</div>;
    return (
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 sm:gap-6">
        {list.map((property) => (
          <PropertyCard
            key={property.id}
            property={property}
            criteria={criteria}
            onShowModal={onShowModal}
          />
        ))}
      </div>
    );
  };
  return (
    <div>
      <div className="mb-6">
        <div className="bg-white rounded-2xl shadow p-2">
          <nav className="flex gap-2" aria-label="Tabs">
            <button
              onClick={() => onTabSwitch(1)}
              className={`flex-1 py-3.5 px-4 rounded-xl font-semibold text-base transition ${
                activeTab === 1
                  ? "tab-active"
                  : "border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              第一梯队
            </button>
            <button
              onClick={() => onTabSwitch(2)}
              className={`flex-1 py-3.5 px-4 rounded-xl font-semibold text-base transition ${
                activeTab === 2
                  ? "tab-active"
                  : "border-transparent text-gray-600 hover:text-gray-800 hover:bg-gray-100"
              }`}
            >
              第二梯队
            </button>
          </nav>
        </div>
      </div>
      <section className="space-y-6">
        <div className={activeTab === 1 ? "" : "hidden"}>
          {renderTier(tier1Props)}
        </div>
        <div className={activeTab === 2 ? "" : "hidden"}>
          {renderTier(tier2Props)}
        </div>
      </section>
    </div>
  );
};

const PropertyForm = ({
  criteria,
  onAddProperty,
  onUpdateProperty,
  onLoadProperties,
  onAddCriteria,
  onDeleteCriteria,
  editingProp,
  onEditingApplied,
}) => {
  const [formData, setFormData] = useState({
    address: "",
    suburb: "",
    tier: "",
    remarks: "",
    url: "",
  });
  const [ratings, setRatings] = useState({});
  const [uploadedImages, setUploadedImages] = useState([]);
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [initialized, setInitialized] = useState(false);

  useEffect(() => {
    if (!initialized && criteria.length > 0) {
      const initialRatings = {};
      criteria.forEach((c) => {
        initialRatings[c.id] = 5;
      });
      setRatings(initialRatings);
      setInitialized(true);
    }
  }, [criteria, initialized]);

  // Apply external editing request from parent (pre-fill the form)
  useEffect(() => {
    if (editingProp && editingProp.id) {
      setFormData({
        address: editingProp.address || "",
        suburb: editingProp.suburb || "",
        tier: String(editingProp.tier || ""),
        remarks: editingProp.remarks || "",
        url: editingProp.url || "",
      });
      setRatings(editingProp.ratings || {});
      setUploadedImages(editingProp.images || []);
      setIsEditing(true);
      setEditingId(editingProp.id);
      if (typeof onEditingApplied === "function") onEditingApplied();
    }
  }, [editingProp, onEditingApplied]);

  useEffect(() => {
    if (initialized && criteria.length > 0) {
      setRatings((prevRatings) => {
        const newRatings = { ...prevRatings };
        criteria.forEach((c) => {
          if (!(c.id in newRatings)) newRatings[c.id] = 5;
        });
        return newRatings;
      });
    }
  }, [criteria, initialized]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleRatingChange = (criteriaId, value) => {
    setRatings((prev) => ({ ...prev, [criteriaId]: parseInt(value) }));
  };

  const handleImageUpload = async (event) => {
    const files = event.target.files;
    if (!files || files.length === 0) return;
    const uploadedUrls = [];
    try {
      for (let i = 0; i < files.length; i++) {
        const file = files[i];
        if (!file.type.startsWith("image/")) continue;
        const formData = new FormData();
        formData.append("image", file, file.name);
        const resp = await fetch(`${env.apiUrl}/api/images/upload`, {
          method: "POST",
          body: formData,
        });
        if (!resp.ok) throw new Error(`上传失败: ${resp.status}`);
        const data = await resp.json();
        if (data && data.url) uploadedUrls.push(data.url);
      }
      if (uploadedUrls.length > 0) setUploadedImages(uploadedUrls);
    } catch (e) {
      console.error(e);
      alert("图片上传失败，请稍后再试");
    }
  };

  const removeImage = (index) => {
    setUploadedImages((prev) => prev.filter((_, i) => i !== index));
  };

  const parseDomainUrl = () => {
    let inputText = formData.url;
    if (!inputText.trim()) {
      alert("请先输入或粘贴文本！");
      return;
    }
    let foundUrl = "";
    let foundAddress = "";
    let foundSuburb = "";
    let foundImages = [];
    const urlMatch = inputText.match(/https?:\/\/[^\s]+domain\.com\.au[^\s]+/i);
    if (urlMatch) {
      foundUrl = urlMatch[0];
    }
    const addressPatterns = [
      /(\d+[-\s]?\d*\s+[A-Za-z\s]+(?:Road|Street|Avenue|Drive|Lane|Court|Place|Boulevard|Crescent|Way))/gi,
      /([A-Za-z\s]+(?:Road|Street|Avenue|Drive|Lane|Court|Place|Boulevard|Crescent|Way)\s+\d+[-\s]?\d*)/gi,
      /(\d+[-\s]?\d*\s+[A-Za-z\s]+)/gi,
    ];
    if (foundUrl) {
      const urlPath = foundUrl.split("/").pop();
      const urlAddressMatch = urlPath.match(/(\d+[-\s]?\d*[-]\w+[-]\w+)/i);
      if (urlAddressMatch) {
        foundAddress = urlAddressMatch[0]
          .replace(/-/g, " ")
          .replace(/\b\w/g, (l) => l.toUpperCase());
      }
    }
    if (!foundAddress) {
      for (let pattern of addressPatterns) {
        const match = inputText.match(pattern);
        if (match) {
          foundAddress = match[0].replace(/\s+/g, " ").trim();
          break;
        }
      }
    }
    const suburbPatterns = [
      /Forest\s*Hill/i,
      /Oakleigh/i,
      /Bentleigh/i,
      /Templestowe\s*Lower/i,
      /Laburnum/i,
      /Canterbury/i,
    ];
    const suburbMapping = {
      canterbury: "其他",
      "forest hill": "Forest Hill",
      oakleigh: "Oakleigh",
      bentleigh: "Bentleigh",
      "templestowe lower": "Templestowe Lower",
      laburnum: "Laburnum",
    };
    if (foundUrl) {
      const urlPath = foundUrl.split("/").pop();
      for (let pattern of suburbPatterns) {
        const match = urlPath.match(pattern);
        if (match) {
          const rawSuburb = match[0];
          foundSuburb = suburbMapping[rawSuburb.toLowerCase()] || rawSuburb;
          break;
        }
      }
    }
    if (!foundSuburb) {
      for (let pattern of suburbPatterns) {
        const match = inputText.match(pattern);
        if (match) {
          const rawSuburb = match[0];
          foundSuburb = suburbMapping[rawSuburb.toLowerCase()] || rawSuburb;
          break;
        }
      }
    }
    const imagePatterns = [
      /https?:\/\/[^\s]+\.(?:jpg|jpeg|png|gif|webp|svg)(?:\?[^\s]*)?/gi,
    ];
    for (let pattern of imagePatterns) {
      const matches = inputText.match(pattern);
      if (matches) {
        foundImages = [...new Set(matches)];
        break;
      }
    }
    setFormData((prev) => ({
      ...prev,
      url: foundUrl || prev.url,
      address: foundAddress || prev.address,
      suburb: foundSuburb || prev.suburb,
    }));
    if (foundImages.length > 0) setUploadedImages(foundImages);
    let hasResults = false;
    let resultMessage = "";
    if (foundUrl) {
      resultMessage += `网址：${foundUrl} `;
      hasResults = true;
    }
    if (foundAddress) {
      resultMessage += `地址：${foundAddress} `;
      hasResults = true;
    }
    if (foundSuburb) {
      resultMessage += `区域：${foundSuburb} `;
      hasResults = true;
    }
    if (foundImages.length > 0) {
      resultMessage += `图片：${foundImages.length}张 `;
      hasResults = true;
    }
    if (hasResults) {
      alert(`智能识别成功：${resultMessage}`);
    } else {
      alert("未识别到有效信息，请手动填写");
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.address || !formData.suburb || !formData.tier) {
      alert("请填写必填字段！");
      return;
    }
    try {
      const propertyData = {
        address: formData.address,
        suburb: formData.suburb,
        tier: parseInt(formData.tier),
        remarks: formData.remarks || null,
        ratings,
        images: uploadedImages,
        url: formData.url || null,
      };
      if (isEditing) {
        await onUpdateProperty(editingId, propertyData);
        setIsEditing(false);
        setEditingId(null);
        alert("房源更新成功！");
      } else {
        await onAddProperty(propertyData);
        alert("房源添加成功！");
      }
      resetForm();
    } catch (error) {
      alert("操作失败：" + error.message);
    }
  };

  const resetForm = () => {
    setFormData({ address: "", suburb: "", tier: "", remarks: "", url: "" });
    setRatings({});
    setUploadedImages([]);
    setIsEditing(false);
    setEditingId(null);
  };

  return (
    <div className="bg-white rounded-lg shadow-md p-6 mb-8">
      <h2 className="text-xl font-semibold mb-4">
        {isEditing ? "编辑房源" : "添加新房源"}
      </h2>
      <form onSubmit={handleSubmit}>
        <div className="mb-5">
          <label className="block text-base font-medium text-gray-700 mb-2">
            房源链接
            <span className="text-sm text-gray-500">(可选，用于快速识别)</span>
          </label>
          <div className="flex flex-col sm:flex-row gap-3">
            <input
              name="url"
              type="text"
              value={formData.url}
              onChange={handleInputChange}
              placeholder="粘贴Domain分享文本，或直接填写链接"
              className="flex-1 px-4 py-3 text-base border-2 border-gray-300 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50"
            />
            <button
              type="button"
              onClick={parseDomainUrl}
              className="bg-indigo-600 text-white px-5 py-3 rounded-xl hover:bg-indigo-700 active:scale-95 font-semibold whitespace-nowrap"
            >
              智能解析
            </button>
          </div>
          <p className="text-sm text-gray-500 mt-2">
            如果智能解析失败，可以手动填写下面的信息
          </p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <label className="block text-sm font-medium mb-2">地址 *</label>
            <input
              name="address"
              type="text"
              value={formData.address}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">区域 *</label>
            <select
              name="suburb"
              value={formData.suburb}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg"
              required
            >
              <option value="">请选择</option>
              <option value="Oakleigh">Oakleigh</option>
              <option value="Bentleigh">Bentleigh</option>
              <option value="Forest Hill">Forest Hill</option>
              <option value="Templestowe Lower">Templestowe Lower</option>
              <option value="Laburnum">Laburnum</option>
              <option value="其他">其他</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">梯队 *</label>
            <select
              name="tier"
              value={formData.tier}
              onChange={handleInputChange}
              className="w-full p-2 border rounded-lg"
              required
            >
              <option value="">请选择</option>
              <option value="1">第一梯队</option>
              <option value="2">第二梯队</option>
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium mb-2">备注</label>
            <textarea
              name="remarks"
              value={formData.remarks}
              onChange={handleInputChange}
              rows="4"
              placeholder="例如：优点-厨房采光好；缺点-离火车站稍远"
              className="w-full px-4 py-3 text-base border-2 border-gray-300 rounded-xl focus:border-indigo-500 focus:ring-2 focus:ring-indigo-500/50 resize-none"
            />
          </div>
        </div>
        {/* 评分区块 */}
        <div className="mt-5">
          <RatingCriteria
            criteria={criteria}
            ratings={ratings}
            onRatingChange={handleRatingChange}
            onAddCriteria={onAddCriteria}
            onDeleteCriteria={onDeleteCriteria}
          />
        </div>
        <div className="mt-6 flex gap-4">
          <button
            type="submit"
            className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700"
          >
            {isEditing ? "更新房源" : "添加房源"}
          </button>
          <button
            type="button"
            onClick={async () => {
              try {
                await onLoadProperties();
                alert("房源列表已刷新！");
              } catch (error) {
                alert("刷新失败：" + error.message);
              }
            }}
            className="bg-gray-600 text-white px-6 py-2 rounded-lg hover:bg-gray-700"
          >
            刷新列表
          </button>
          <button
            type="button"
            onClick={resetForm}
            className="bg-yellow-500 text-white px-6 py-2 rounded-lg hover:bg-yellow-600"
          >
            重置表单
          </button>
        </div>
        <div className="mt-5">
          <label className="block text-base font-medium text-gray-700 mb-2">
            图片 <span className="text-sm text-gray-500">(支持多张)</span>
          </label>
          <div className="space-y-3">
            <div className="text-center">
              <input
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={handleImageUpload}
                id="prop-image-upload"
              />
              <label
                htmlFor="prop-image-upload"
                className="inline-flex items-center px-4 py-2 bg-indigo-600 text-white font-medium rounded-lg hover:bg-indigo-700 active:scale-95 transition-all cursor-pointer"
              >
                📁 上传图片
              </label>
              <div className="text-xs text-gray-500 mt-2">
                💡 支持多张图片，按住 Ctrl/Cmd 键可选择多张
              </div>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 mt-2">
              {uploadedImages.map((image, index) => (
                <div key={index} className="relative group">
                  <img
                    src={image}
                    alt="预览图"
                    className="w-full h-20 object-cover rounded-lg border border-gray-200"
                    onError={(e) => {
                      e.target.src =
                        "https://placehold.co/200x200/e2e8f0/adb5bd?text=Image+Error";
                    }}
                  />
                  <button
                    type="button"
                    onClick={() => removeImage(index)}
                    className="absolute -top-2 -right-2 bg-red-500 text-white rounded-full w-6 h-6 text-sm opacity-0 group-hover:opacity-100 transition-opacity"
                  >
                    ×
                  </button>
                </div>
              ))}
            </div>
          </div>
        </div>
      </form>
    </div>
  );
};

// Inline Detail Modal with Image Carousel
const DetailModal = ({ property, onClose, onSave, onDelete, criteria }) => {
  const images = Array.isArray(property?.images) ? property.images : [];
  const [index, setIndex] = useState(0);
  const total = images.length;
  const [isEditing, setIsEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState({
    address: property.address || "",
    suburb: property.suburb || "",
    tier: property.tier || 1,
    remarks: property.remarks || "",
    ratings: property.ratings || {},
  });

  useEffect(() => {
    if (total > 0 && index >= total) setIndex(0);
  }, [total, index]);

  const prev = () => setIndex((i) => (i - 1 + total) % total);
  const next = () => setIndex((i) => (i + 1) % total);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  };

  const handleRating = (rid, value) => {
    setForm((prev) => ({
      ...prev,
      ratings: { ...prev.ratings, [rid]: Number(value) },
    }));
  };

  const handleSave = async () => {
    try {
      setSaving(true);
      await onSave({
        address: form.address,
        suburb: form.suburb,
        tier: Number(form.tier),
        remarks: form.remarks || null,
        ratings: form.ratings || {},
        images: property.images || [],
        url: property.url || null,
      });
      setIsEditing(false);
    } catch (e) {
      alert("保存失败：" + e.message);
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="modal-overlay" role="dialog" aria-modal="true">
      <div className="modal-content p-6">
        <div className="flex justify-between items-center mb-4">
          <h3 className="text-2xl font-bold text-gray-800">房源详情</h3>
          <div className="flex items-center gap-2">
            {!isEditing ? (
              <>
                <button
                  onClick={() => setIsEditing(true)}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded-lg"
                >
                  编辑
                </button>
                <button
                  onClick={async () => {
                    if (!window.confirm("确认删除该房源？")) return;
                    try {
                      await onDelete();
                      onClose();
                    } catch (e) {
                      alert("删除失败：" + e.message);
                    }
                  }}
                  className="px-3 py-1.5 bg-red-600 text-white rounded-lg"
                >
                  删除
                </button>
              </>
            ) : (
              <>
                <button
                  onClick={handleSave}
                  disabled={saving}
                  className="px-3 py-1.5 bg-green-600 text-white rounded-lg disabled:opacity-60"
                >
                  {saving ? "保存中..." : "保存"}
                </button>
                <button
                  onClick={() => setIsEditing(false)}
                  className="px-3 py-1.5 bg-gray-200 text-gray-700 rounded-lg"
                >
                  取消
                </button>
              </>
            )}
            <button
              onClick={onClose}
              className="text-gray-500 hover:text-gray-800 text-xl"
            >
              ×
            </button>
          </div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div>
            {total > 0 ? (
              <div className="carousel">
                <button
                  onClick={prev}
                  className="px-3 py-2 bg-gray-100 rounded-lg"
                >
                  ‹
                </button>
                <img
                  src={images[index]}
                  alt={`图片 ${index + 1}`}
                  className="w-full max-h-[420px] object-contain"
                  onError={(e) => {
                    e.currentTarget.src =
                      "https://placehold.co/800x420/e2e8f0/adb5bd?text=Image+Error";
                  }}
                />
                <button
                  onClick={next}
                  className="px-3 py-2 bg-gray-100 rounded-lg"
                >
                  ›
                </button>
              </div>
            ) : (
              <div className="bg-gray-50 rounded-xl h-64 flex items-center justify-center text-gray-500">
                暂无图片
              </div>
            )}
          </div>
          <div>
            {!isEditing ? (
              <>
                <div className="space-y-2 mb-4">
                  <div className="text-sm text-indigo-600 font-semibold">
                    {property.suburb}
                  </div>
                  <div
                    className="text-xl font-bold text-gray-800"
                    title={property.address}
                  >
                    {property.address}
                  </div>
                  <div className="text-gray-600">第{property.tier}梯队</div>
                  {property.url ? (
                    <a
                      href={property.url}
                      target="_blank"
                      rel="noreferrer"
                      className="text-blue-600 underline"
                    >
                      打开房源链接
                    </a>
                  ) : null}
                </div>
                <div className="space-y-2 mb-4">
                  {property.remarks ? (
                    <div className="bg-gray-50 p-4 rounded-xl text-gray-700 whitespace-pre-wrap leading-relaxed">
                      {property.remarks}
                    </div>
                  ) : (
                    <div className="bg-gray-50 p-4 rounded-xl text-gray-500">
                      暂无备注
                    </div>
                  )}
                </div>
                <div className="space-y-2">
                  {(property.ratings ? Object.keys(property.ratings) : []).map(
                    (rid) => (
                      <div
                        key={rid}
                        className="flex justify-between items-center py-1 border-b border-gray-100 last:border-b-0"
                      >
                        <span className="text-gray-700">{rid}</span>
                        <span className="font-semibold text-indigo-600">
                          {property.ratings[rid]}/10
                        </span>
                      </div>
                    )
                  )}
                </div>
              </>
            ) : (
              <>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-4">
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      地址
                    </label>
                    <input
                      name="address"
                      value={form.address}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      区域
                    </label>
                    <select
                      name="suburb"
                      value={form.suburb}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg"
                    >
                      <option value="Oakleigh">Oakleigh</option>
                      <option value="Bentleigh">Bentleigh</option>
                      <option value="Forest Hill">Forest Hill</option>
                      <option value="Templestowe Lower">
                        Templestowe Lower
                      </option>
                      <option value="Laburnum">Laburnum</option>
                      <option value="其他">其他</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium mb-1">
                      梯队
                    </label>
                    <select
                      name="tier"
                      value={form.tier}
                      onChange={handleChange}
                      className="w-full p-2 border rounded-lg"
                    >
                      <option value={1}>第一梯队</option>
                      <option value={2}>第二梯队</option>
                    </select>
                  </div>
                  {/* URL 不在弹层编辑，保持只读处显示/隐藏 */}
                </div>
                <div className="mb-4">
                  <label className="block text-sm font-medium mb-1">备注</label>
                  <textarea
                    name="remarks"
                    rows="4"
                    value={form.remarks}
                    onChange={handleChange}
                    className="w-full p-2 border rounded-lg"
                  />
                </div>
                <div className="space-y-2">
                  {(criteria || []).map((c) => (
                    <div key={c.id} className="flex items-center gap-3 py-1">
                      <span className="text-gray-700 flex-1">{c.label}</span>
                      <input
                        type="range"
                        min="1"
                        max="10"
                        step="1"
                        value={form.ratings?.[c.id] ?? 5}
                        onChange={(e) => handleRating(c.id, e.target.value)}
                        className="accent-pink-600 flex-1"
                      />
                      <span className="w-10 text-right font-semibold text-indigo-600">
                        {form.ratings?.[c.id] ?? 5}
                      </span>
                    </div>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Main App (keep only primary features)
const AppSingle = () => {
  const [activeTab, setActiveTab] = useState(1);
  const [showModal, setShowModal] = useState(false);
  const [selectedProperty, setSelectedProperty] = useState(null);
  const [editingProp, setEditingProp] = useState(null);

  const {
    properties,
    fetchProperties,
    addProperty,
    updateProperty,
    deleteProperty,
  } = useProperties();
  const { criteria, fetchCriteria, addCriteria, deleteCriteria } =
    useCriteria();

  return (
    <div className="bg-gray-50 min-h-screen">
      <GlobalStyles />
      <div className="container mx-auto px-4 py-8">
        <div className="flex flex-col lg:flex-row gap-8" id="layout-container">
          <div className="w-full lg:w-1/3" id="left-panel">
            <PropertyForm
              criteria={criteria}
              onAddProperty={addProperty}
              onUpdateProperty={updateProperty}
              onLoadProperties={fetchProperties}
              onAddCriteria={addCriteria}
              onDeleteCriteria={deleteCriteria}
              editingProp={editingProp}
              onEditingApplied={() => setEditingProp(null)}
            />
          </div>
          <div className="w-full lg:w-2/3" id="right-panel">
            <PropertyList
              properties={properties}
              criteria={criteria}
              activeTab={activeTab}
              onTabSwitch={setActiveTab}
              onShowModal={(p) => {
                setSelectedProperty(p);
                setShowModal(true);
              }}
            />
          </div>
        </div>
      </div>
      {showModal && selectedProperty ? (
        <DetailModal
          property={selectedProperty}
          criteria={criteria}
          onSave={async (updates) => {
            const id = selectedProperty.id;
            await updateProperty(id, updates);
            await fetchProperties();
            setSelectedProperty((prev) => ({ ...prev, ...updates }));
          }}
          onDelete={async () => {
            await deleteProperty(selectedProperty.id);
            await fetchProperties();
          }}
          onClose={() => {
            setShowModal(false);
            setSelectedProperty(null);
          }}
        />
      ) : null}
    </div>
  );
};

export default AppSingle;
